import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { eventService } from '@/services/eventService';
import { EventSchedule } from '@/types';
import { supabaseErrorToToast } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';

export function useEventSchedule(eventId: string | undefined) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // Fetch schedule items
  const {
    data: scheduleItems,
    isLoading: isLoadingSchedule,
    error: scheduleError,
    refetch
  } = useQuery({
    queryKey: ['eventSchedule', eventId],
    queryFn: () => {
      if (!eventId) return Promise.resolve([]);
      return eventService.getEventSchedule(eventId);
    },
    enabled: !!eventId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Create schedule item
  const createMutation = useMutation({
    mutationFn: (newItem: Omit<EventSchedule, 'id'>) => {
      setIsLoading(true);
      return eventService.createEventScheduleItem(newItem);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventSchedule', eventId] });
      setIsLoading(false);
      toast({
        title: 'Actividad agregada',
        description: 'La actividad ha sido agregada al cronograma',
      });
    },
    onError: (error) => {
      setIsLoading(false);
      supabaseErrorToToast(error);
    },
  });

  // Update schedule item
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string, data: Partial<Omit<EventSchedule, 'id' | 'event_id'>> }) => {
      setIsLoading(true);
      return eventService.updateEventScheduleItem(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventSchedule', eventId] });
      setIsLoading(false);
      toast({
        title: 'Guardado',
        description: 'Los cambios han sido guardados',
      });
    },
    onError: (error) => {
      setIsLoading(false);
      supabaseErrorToToast(error);
    },
  });

  // Delete schedule item
  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      setIsLoading(true);
      return eventService.deleteEventScheduleItem(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventSchedule', eventId] });
      setIsLoading(false);
      toast({
        title: 'Actividad eliminada',
        description: 'La actividad ha sido eliminada del cronograma',
      });
    },
    onError: (error) => {
      setIsLoading(false);
      supabaseErrorToToast(error);
    },
  });

  // Update order of items
  const updateOrderMutation = useMutation({
    mutationFn: async (items: EventSchedule[]) => {
      setIsLoading(true);
      // Update each item with its new order
      const updatePromises = items.map((item, index) => 
        eventService.updateEventScheduleItem(item.id, { orden: index + 1 })
      );
      return Promise.all(updatePromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventSchedule', eventId] });
      setIsLoading(false);
      toast({
        title: 'Guardado',
        description: 'El orden ha sido actualizado',
      });
    },
    onError: (error) => {
      setIsLoading(false);
      supabaseErrorToToast(error);
    },
  });

  // Handle errors
  if (scheduleError) {
    supabaseErrorToToast(scheduleError);
  }

  return {
    scheduleItems: scheduleItems || [],
    isLoading: isLoadingSchedule || isLoading,
    createScheduleItem: createMutation.mutate,
    updateScheduleItem: updateMutation.mutate,
    deleteScheduleItem: deleteMutation.mutate,
    updateItemsOrder: updateOrderMutation.mutate,
    refetch,
  };
} 